import { useEffect } from 'react'

import { FixedBanner, Heading } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { FullScreen } from '@modals/layout'
import { routes } from '@services/sections/modal/routes'
import { TypeGuards } from '@services/type-guards'
import { CHAINED_TRANSFER_POLL_INTERVAL } from '@shared/apollo/pollInterval'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { Center } from '@shared/components/layout'
import { TransferStatus, useChainedTransferQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

export interface SendProps {
  /**
   * Transfer ID.
   */
  id?: string
}

export const Send: React.FC<SendProps> = ({ id }) => {
  const { t } = useTranslation('ace')
  const { router } = useModal()

  const { data, error, refetch } = useChainedTransferQuery({
    variables: {
      id: id ?? '',
    },
    pollInterval: CHAINED_TRANSFER_POLL_INTERVAL,
  })

  useEffect(() => {
    const { destination, status } = data?.chainedTransfer ?? {}

    // If transfer failed, push to failure route
    if (status === TransferStatus.failed) {
      return router.push(routes.blockchain.aceSendOnchain.failure)
    }

    // If transfer is not complete or destination is not chained blockchain location
    // Keep polling
    if (
      !TypeGuards.TransferLocation.isChainedBlockchainLocation(destination) ||
      status !== TransferStatus.complete
    ) {
      return
    }

    // Burn is complete but mint is incomplete
    // Push to receive route for user to perform mint in wallet
    if (!destination.isCompleteAceTransfer) {
      return router.push({
        pathname: routes.blockchain.aceSendOnchain.receive,
        query: {
          transferMetadata: destination.metadata,
          address: destination.address,
          chain: destination.chain,
          description: destination.description,
        },
      })
    }

    // If isCompleteAceTransfer, the user should not be in this flow
    // In this edge case, push to success route
    return router.push(routes.blockchain.aceSendOnchain.success)
  }, [data?.chainedTransfer, router])

  return (
    <FullScreen totalSteps={2}>
      <Center className="flex flex-col justify-items-center">
        <GraphQLErrorBoundary error={error} retry={refetch}>
          <Heading
            alignment="center"
            iconName="CircleSpinnerSolid"
            layout="vertical"
            variant="page/sm"
            loop
          >
            <Heading.Header as="h2">
              {t(`sendOnchain.send.heading`)}
            </Heading.Header>
            <Heading.Intro as="p" className="w-168">
              {t(`sendOnchain.send.subheading`)}
            </Heading.Intro>
          </Heading>

          <div className="mt-12">
            <FixedBanner className="w-fit" status="warning" visible>
              <FixedBanner.Title>
                {t(`sendOnchain.send.banner.title`)}
              </FixedBanner.Title>
              <FixedBanner.Description>
                {t(`sendOnchain.send.banner.description`)}
              </FixedBanner.Description>
            </FixedBanner>
          </div>
        </GraphQLErrorBoundary>
      </Center>
    </FullScreen>
  )
}
